<script lang="ts">
import type { QuerySchemaWalker, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { CalendarDisplayOptions } from '#layers/listing/types/layout';
import { CollectionReads, DisplayTemplateInput, PrimitiveFieldSelect, resolveCollectionDisplayTemplate, usePrimitiveField, useWorkspaceSchemaStore } from '#layers/data-model/public';

interface InitializeLayoutCalendarOptionsParams {
	collection: ResolvedMonospaceCollection;
	options?: Partial<CalendarDisplayOptions> | null;
	/** Whether events may be placed by a field, see `useCalendarDateFieldGate`. */
	allowsStartField: (field: ResolvedMonospacePrimitiveField) => boolean;
	/** The workspace query schema, which decides the collection's display template, see `resolveCollectionDisplayTemplate`. */
	querySchema: QuerySchemaWalker | null | undefined;
}

export function getLocaleFirstDayOfWeek(): 0 | 1 {
	try {
		const locale = new Intl.Locale(navigator.language) as any;
		const firstDay: number | undefined = locale.getWeekInfo?.().firstDay ?? locale.weekInfo?.firstDay;

		if (firstDay === undefined) return 0;
		return firstDay === 7 ? 0 : 1;
	}
	catch {
		return 0;
	}
}

export function initializeLayoutCalendarOptions({ collection, options, allowsStartField, querySchema }: InitializeLayoutCalendarOptionsParams): CalendarDisplayOptions {
	return {
		startDateField: options?.startDateField ?? collection.primitiveFields.find((field) => allowsStartField(field))?.id ?? null,
		endDateField: options?.endDateField ?? null,
		titleTemplate: options?.titleTemplate ?? resolveCollectionDisplayTemplate(collection, CollectionReads.Many, querySchema),
		firstDayOfWeek: options?.firstDayOfWeek ?? 0,
		viewMode: options?.viewMode ?? 'month',
		currentDate: options?.currentDate,
	};
}
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { useCalendarDateFieldGate } from '#layers/listing/composables/use-calendar-date-field-gate';
import { useInitializedDisplayOptionsModel } from '#layers/listing/composables/use-initialized-display-options-model';

interface CalendarDisplayOptionProps {
	collection: ResolvedMonospaceCollection;
	displayOptions?: CalendarDisplayOptions;
	sharedDisplayOptions?: CalendarDisplayOptions;
}

const props = defineProps<CalendarDisplayOptionProps>();

const emit = defineEmits<{
	'update:displayOptions': [value: CalendarDisplayOptions | undefined];
}>();

const dateFieldGate = useCalendarDateFieldGate(() => props.collection);
const workspaceSchema = useWorkspaceSchemaStore();

const model = useInitializedDisplayOptionsModel(
	{
		baselineDisplayOptions: () => props.sharedDisplayOptions,
		displayOptions: () => props.displayOptions,
		initializedBaselineOptions: () => initializeLayoutCalendarOptions({ collection: props.collection, options: props.sharedDisplayOptions, allowsStartField: dateFieldGate.allowsStartField, querySchema: workspaceSchema.querySchema }),
		initializedOptions: () => initializeLayoutCalendarOptions({ collection: props.collection, options: props.displayOptions, allowsStartField: dateFieldGate.allowsStartField, querySchema: workspaceSchema.querySchema }),
	},
	(value) => emit('update:displayOptions', value),
);

const collectionDisplayTemplate = computed(() => resolveCollectionDisplayTemplate(props.collection, CollectionReads.Many, workspaceSchema.querySchema));

const startField = usePrimitiveField(() => model.value?.startDateField);

function allowsEndField(field: ResolvedMonospacePrimitiveField) {
	return dateFieldGate.allowsEndField(field, startField.value);
}
</script>

<template>
	<template v-if="model">
		<DFormField :label="$t('layout.calendar.title_field')">
			<DisplayTemplateInput
				v-model="model.titleTemplate"
				:default-value="collectionDisplayTemplate"
				:collection
			/>
		</DFormField>
		<DFormField :label="$t('layout.calendar.start_date_field')">
			<PrimitiveFieldSelect
				v-model="model.startDateField"
				:collection-id="collection.id"
				:filter="dateFieldGate.allowsStartField"
			/>
		</DFormField>
		<DFormField :label="$t('layout.calendar.end_date_field')">
			<PrimitiveFieldSelect
				v-model="model.endDateField"
				:collection-id="collection.id"
				:filter="allowsEndField"
				clear
			/>
		</DFormField>
		<DFormSeparator class="-mx-4 my-1" />
		<DFormField :label="$t('layout.calendar.first_day_of_week.label')" layout="inline">
			<div class="flex">
				<DSelect
					v-model="model.firstDayOfWeek"
					class="ms-auto w-fit"
					:items="[
						{ label: $t('layout.calendar.first_day_of_week.options.sunday'), value: 0 },
						{ label: $t('layout.calendar.first_day_of_week.options.monday'), value: 1 },
					]"
				/>
			</div>
		</DFormField>
	</template>
</template>
